/**
 * CitationSuggester Component
 * Button/UI element that triggers citation suggestions for selected text
 */

import React, { useState } from 'react'
import { logError } from '@utils/logger'
import { motion } from 'framer-motion'
import { BookOpenIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { useQueryClient } from '@tanstack/react-query'

import { citationsAPI, CitationSuggestionRequest } from '@/api/citations'
import { CitationPanel } from './CitationPanel'

interface CitationSuggesterProps {
  selectedText: string
  documentContext?: string
  researchField?: string
  onCitationInsert?: (citation: string, paper?: any) => void
  documentMode?: 'markdown' | 'latex' | 'plain'
  documentId?: string | null
}

export const CitationSuggester: React.FC<CitationSuggesterProps> = ({
  selectedText,
  documentContext,
  researchField,
  onCitationInsert,
  documentMode = 'markdown',
  documentId,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [suggestions, setSuggestions] = useState<any[]>([])
  const [showPanel, setShowPanel] = useState(false)
  const [currentSelectedText, setCurrentSelectedText] = useState<string>('')
  const queryClient = useQueryClient()

  const handleSuggestCitations = async () => {
    if (!selectedText || selectedText.trim().length < 10) {
      return
    }

    // If panel is already open with different text, show message
    if (showPanel && currentSelectedText !== selectedText) {
      return
    }
    
    // If panel is already open with same text, just ensure it's maximized (handled by panel)
    if (showPanel && currentSelectedText === selectedText) {
      // Panel is already open - user can maximize it manually if minimized
      return
    }

    setIsLoading(true)
    setShowPanel(true)
    setCurrentSelectedText(selectedText)

    try {
      const request: CitationSuggestionRequest = {
        text: selectedText,
        context: documentContext,
        max_results: 5,
        field: researchField,
        citation_mode: 'statement', // Default to statement mode for in-text citations
      }

      const response = await citationsAPI.suggestCitations(request)

      if (response.success && response.suggestions.length > 0) {
        setSuggestions(response.suggestions)
        
        // Invalidate token usage queries after successful citation suggestion
        if (documentId) {
          queryClient.invalidateQueries({ queryKey: ['token-usage', 'document', documentId] })
        }
        queryClient.invalidateQueries({ queryKey: ['token-usage', 'user-stats'] })
      } else {
        setSuggestions([])
      }
    } catch (error: any) {
      logError('Citation suggestion error:', error)
      setSuggestions([])
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={handleSuggestCitations}
        disabled={isLoading || !selectedText || selectedText.trim().length < 10}
        className="group relative flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-150 text-amber-600 hover:bg-amber-50 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-sm"
        title="Suggest citations for selected text"
      >
        {isLoading ? (
          <ArrowPathIcon className="w-4 h-4 animate-spin" />
        ) : (
          <BookOpenIcon className="w-4 h-4" />
        )}
        <span className="hidden sm:inline">
          {isLoading ? 'Finding...' : 'Citations'}
        </span>
        
        {/* Tooltip */}
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
          Find relevant academic papers
        </div>
      </motion.button>

      {/* Citation Panel */}
      {showPanel && (
        <CitationPanel
          isOpen={showPanel}
          onClose={() => {
            setShowPanel(false)
            setCurrentSelectedText('')
            setSuggestions([])
          }}
          suggestions={suggestions}
          isLoading={isLoading}
          selectedText={currentSelectedText || selectedText}
          onCitationInsert={onCitationInsert}
          documentMode={documentMode}
          documentId={documentId}
        />
      )}
    </>
  )
}

